import { AlertState } from 'Components/Organisms/Alert';
import { Severity } from 'Contexts/RestaurantContext';
import { SignatureContext } from 'Contexts/SignatureContext';
import {
  RequestV03Document,
  WaitingListEntryDocument,
} from 'types/shared/documents';
import { RequestStatus } from 'types/shared/helper/reservations';
import React, { useContext, useEffect, useRef, useState } from 'react';
import {
  EditReservation,
  Reservation,
  ReservationStatus,
  ResSettings,
  TimeSlot,
} from 'types/reservations';
import { db } from 'utils/firebase';
import { actingAs } from 'utils/reservationAuthor';
import {
  createRandomId,
  hasBackendOwnedHold,
  timeHelper,
  timeToBlock,
} from 'utils/helper';
import server from 'utils/server';
import useToast from './useToast';
import useDateNow from './useDateNow';
import { useRouter } from './useRouter';
import useRestaurant from './useRestaurant';
import useAlert from './useAlert';
import usePinAuth from './usePinAuth';
import {
  getTicketPartialPaymentFraction,
  getTicketPayableAmount,
  getTicketPaymentAmount,
  getTicketPaymentSummary,
  getTicketSelectionError,
  getTicketValidTillMinutes,
  normalizeTicketSelection,
} from 'utils/ticketSession';

export type ReservationSettings = {
  pendingTill?: number | undefined;
  standardFloorPlan?: string | undefined;
  maxGuestsOnWebsite?: number | undefined;
  hideResLengthInEmails?: boolean | undefined;
  phoneNumberMandatory?: boolean | undefined;
  addUserDetails?: boolean | undefined;
  signatureRequired?: boolean | undefined;
  customAttr?: string[] | undefined;
  minTimeInAdvance?: number | undefined;
  emailMandatory?: boolean;
  shuffleMode?: boolean;
  blocksBetweenReservations?: number;
};

const EMPTY_GUEST = {
  name: 'Wird gerade erstellt',
  email: '',
  phone: '',
  comment: '',
  preferredLanguage: 'de',
  sendEmail: false,
  attr: [],
};

const isTicketSessionMode = (mode?: EditReservation['ticketMode']) =>
  mode === 'pay_at_restaurant' || mode === 'payment_link';

const useEditReservation = (
  restaurant: string | null,
  updatedBy: string | null,
  alert: React.Dispatch<React.SetStateAction<AlertState | null>>,
  currentDate: string,
  setcurrentReservation: React.Dispatch<React.SetStateAction<string | null>>,
  settings?: ReservationSettings,
  isLightReservation?: boolean,
  tableGuestsMap?: Record<string, number>,
  reservationLengthPerSizeMap?: Record<number, number>
) => {
  const { experimentalFlags } = useRestaurant();

  const { validateSignature } = useContext(SignatureContext);

  const { authenticate } = usePinAuth();

  const sendConfirmationEmailDefault =
    !experimentalFlags?.data?.confirmationEmailOffByDefault;

  const [editReservation, seteditReservation] =
    useState<null | EditReservation>(null);

  const [signaturePromptOpen, setSignaturePromptOpen] =
    useState<boolean>(false);

  const [originalUrl, setoriginalUrl] = useState<string | null>(null);

  const toast = useToast();

  const DateNow = useDateNow();

  const navigate = useRouter();

  // Safety net for abandoned creates: step 1 writes a placeholder doc, but
  // only the sidebar X-button deletes it. Any other way out (row click, date
  // switch, screen change) leaks the placeholder as a stuck ghost until the
  // day-view cleanup. Watch the create-session id and delete the doc when the
  // session ends without finalize, but never when the guest data was touched.
  const createSessionRef = useRef<string | null>(null);
  const finalizedRef = useRef<string | null>(null);

  useEffect(() => {
    const current =
      editReservation?.new && !editReservation.walkin
        ? editReservation.reservationId ?? null
        : null;
    const previous = createSessionRef.current;

    createSessionRef.current = current;

    if (
      !previous ||
      previous === current ||
      previous === finalizedRef.current ||
      // The same doc reopened as a normal edit (expired hold) is in use, not
      // abandoned.
      previous === editReservation?.reservationId
    )
      return;

    const ref = db.collection('requestsV03').doc(previous);

    // Transaction, not get-then-delete: a finalize landing between the two
    // would otherwise let us delete a reservation that just got real guest
    // data. The read is revalidated at commit, so the delete only happens
    // while the doc is still an untouched placeholder.
    db.runTransaction(async (tx) => {
      const doc = await tx.get(ref);
      const data = doc.data();

      if (
        doc.exists &&
        data?.guest?.name === EMPTY_GUEST.name &&
        !hasBackendOwnedHold(data)
      ) {
        tx.delete(ref);
      }
    }).catch((err) => {
      // The day-view cleanup can remove the same placeholder first, which
      // makes the delete fail. Log instead of leaving a silent rejection.
      console.error('Placeholder cleanup failed', err);
    });
  }, [
    editReservation?.reservationId,
    editReservation?.new,
    editReservation?.walkin,
  ]);

  const goBackToOriginal = () => {
    if (!originalUrl) return;
    if (!navigate.pathname.endsWith(originalUrl.split('?')[0])) {
      navigate.push(
        `/${restaurant}/reservations/${currentDate}/${originalUrl}`
      );
    }
    setoriginalUrl(null);
  };

  const handleRelocate = (
    reservation: Reservation | null,
    keepTables = reservation?.fixed ?? false
  ) => {
    // Active holds stay locked (someone is mid-create); expired ones must
    // open for editing, otherwise a stuck placeholder can never be repaired.
    const expiredHold =
      !!reservation?.validTill &&
      reservation.validTill <= DateNow() &&
      !hasBackendOwnedHold(reservation);

    if (reservation?.validTill && !reservation.ticketId && !expiredHold) {
      toast('Diese Reservierung kann nicht bearbeitet werden.', Severity.ERROR);
      return;
    }

    if (reservation === null) {
      seteditReservation(null);
    } else {
      let {
        id,
        date,
        time,
        tables = [],
        reservationLength,
        occassion,
        guests,
        guest,
        walkIn,
        tableStr,
        ticket,
        ticketId,
      } = reservation;
      const ticketEvent =
        ticket?.event ?? (reservation.ticketId ? reservation.event ?? null : null);
      const ticketMode = ticket
        ? ticket.payAtRestaurant
          ? 'pay_at_restaurant'
          : 'payment_link'
        : undefined;
      const ticketCouponAmount = getTicketPaymentAmount(ticket, 'coupon');
      const ticketVoucherAmount = getTicketPaymentAmount(ticket, 'voucher');

      let timeSlot = {
        time,
        block: timeToBlock(time),
        reservationLength,
        blockedSlots: 0,
        combinations: [],
        maxGuestsReached: false,
        totalSlots: 0,
      };

      console.log({
        keepTables,
      });

      seteditReservation({
        date,
        time,
        tables,
        tableStr,
        reservationLength,
        reservationId: id,
        occassion,
        guests,
        guest,
        walkin: walkIn,
        timeSlot,
        keepTables,
        // Only heal holds that already carry real guest data. An untouched
        // placeholder must stay expired so the cleanup can still delete it.
        clearValidTill: expiredHold && guest?.name !== EMPTY_GUEST.name,
        sendConfirmationEmail: sendConfirmationEmailDefault && !!guest?.email,
        ticketId: ticketId ?? ticket?.id ?? null,
        event: ticketEvent,
        ticketMode,
        ticketOriginalAmount: ticket ? getTicketPayableAmount(ticket) : undefined,
        ticketOriginalCouponAmount: ticketCouponAmount,
        ticketOriginalVoucherAmount: ticketVoucherAmount,
        ticketCouponCode: (ticket as any)?.coupon_code ?? undefined,
        ticket: ticket
          ? {
              options: ticket.options ?? {},
              optionalAddOns: ticket.optionalAddOns ?? {},
            }
          : undefined,
        ticketPartialPayment: ticket?.partialPayment ?? 1,
        ticketCanEdit: (ticket as any)?.paymentRequest?.canEdit ?? true,
      });
    }
  };

  // const walkinResLength =

  const handleNewReservation = (
    date: string,
    walkin = false,
    tables: string[] | null = null,
    occassion?: string,
    space?: string
  ) => {
    let time: string | undefined = undefined,
      timeSlot: TimeSlot | null = null,
      guests: number | undefined = undefined,
      reservationLength: number | undefined = undefined;

    if (walkin) {
      time = timeHelper();

      timeSlot = {
        block: timeToBlock(time),
        blockedSlots: 0,
        combinations: [],
        maxGuestsReached: false,
        time,
        reservationLength: 4,
        totalSlots: 0,
      };

      if (!!experimentalFlags?.data?.walkinDefaultOccassion) {
        occassion = experimentalFlags.data.walkinDefaultOccassion;
      }

      const guestMapValue = tableGuestsMap?.[tables?.[0] ?? ''];

      if (guestMapValue) {
        guests = guestMapValue;

        let resLength = reservationLengthPerSizeMap?.[guestMapValue];

        console.log({ resLength, guestMapValue, reservationLengthPerSizeMap });

        if (resLength) {
          timeSlot.reservationLength = resLength;
          reservationLength = resLength;
        }
      } else {
        alert({
          title: 'How many guests are there?',
          description: 'Please enter the number of guests for the reservation',
          descriptionTranslation: 'reservations',
          titleTranslation: 'reservations',
          inputFieldLabel: 'Number of guests',
          inputFieldLabelTranslation: 'reservations',
          onSubmit: (_, value) => {
            seteditReservation({
              date,
              tables,
              guest: null,
              space,
              time,
              occassion,
              walkin,
              timeSlot,
              reservationLength: undefined,
              page: 0,
              new: true,
              guests: Number(value || 2),
              sendConfirmationEmail: sendConfirmationEmailDefault,
              keepTables: walkin,
            });
          },
          actions: [
            {
              id: 'cancel',
              label: 'Cancel',
              onClick: () => {},
            },
            {
              id: 'submit',
              label: 'Next',
              submit: true,
            },
          ],
        });

        return;
      }
    }

    seteditReservation({
      date,
      tables,
      guest: null,
      space,
      time,
      occassion,
      walkin,
      timeSlot,
      reservationLength,
      page: 0,
      new: true,
      guests,
      sendConfirmationEmail: sendConfirmationEmailDefault,
      keepTables: walkin,
    });
  };

  const submitReservation = async (
    sendConfirmationEmail = false,
    signatureOverride?: string
  ) => {
    if (!editReservation?.timeSlot) return;

    let {
      time,
      block: startTimeInBlocks,
      // reservationLength,
    } = editReservation.timeSlot;

    let {
      guests = 0,
      date,
      occassion,
      tables = null,
      reservationId,
      space = null,
      waiterSignature: existingSignature,
      tableStr = null,
      sendNoShowSMS = false,
      reservationLength = editReservation?.timeSlot?.reservationLength ?? 4,
    } = editReservation;

    const waiterSignature = signatureOverride ?? existingSignature;

    const createTicketSession = async (finalReservation?: {
      guest?: EditReservation['guest'];
      customData?: EditReservation['customData'];
    }) => {
      if (
        !restaurant ||
        !reservationId ||
        !isTicketSessionMode(editReservation.ticketMode)
      ) {
        return;
      }

      const eventId = editReservation.event?.id ?? editReservation.timeSlot?.eventId;

      if (!eventId) {
        throw new Error('Der gewählte Slot hat kein Event mehr.');
      }

      const ticket = editReservation.event
        ? normalizeTicketSelection(
            editReservation.event,
            guests,
            editReservation.ticket
          )
        : editReservation.ticket ?? {
            options: {},
            optionalAddOns: {},
          };
      const ticketSummary = editReservation.event
        ? getTicketPaymentSummary({
            event: editReservation.event,
            guests,
            ticket,
            coupon: editReservation.ticketCoupon,
            voucher: editReservation.ticketVoucher,
            partialPayment: 1,
            includeServiceFee: false,
          })
        : null;
      const partialPayment =
        editReservation.ticketMode === 'payment_link' && ticketSummary
          ? getTicketPartialPaymentFraction(
              editReservation,
              ticketSummary.ticketTotal
            )
          : 1;
      const validTillMinutes =
        editReservation.ticketMode === 'payment_link'
          ? getTicketValidTillMinutes(
              editReservation.ticketValidTillValue ?? 2,
              editReservation.ticketValidTillUnit ?? 'hours'
            )
          : 0;

      if (editReservation.ticketId) {
        await server.post(
          `/v03/reservations/${restaurant}/res/${reservationId}/ticketOverview`,
          {
            uid: updatedBy,
            guests,
            ticket,
            canEdit: !!editReservation.ticketCanEdit,
          }
        );

        return;
      }

      await server.post(
        `/v03/reservations/${restaurant}/res/${reservationId}/ticketSession`,
        {
          uid: updatedBy,
          mode: editReservation.ticketMode,
          eventId,
          ticket,
          coupon_code: editReservation.ticketCouponCode || null,
          voucher_code: editReservation.ticketVoucherCode || null,
          partialPayment,
          validTillMinutes,
          sendVia:
            editReservation.ticketMode === 'payment_link'
              ? editReservation.ticketSendVia ?? 'email'
              : 'none',
          canEdit: !!editReservation.ticketCanEdit,
          ...(finalReservation?.guest ? { guest: finalReservation.guest } : {}),
          ...('customData' in (finalReservation ?? {})
            ? { customData: finalReservation?.customData ?? {} }
            : {}),
        }
      );
    };

    const validateTicketSession = () => {
      if (!isTicketSessionMode(editReservation.ticketMode)) return true;

      const event = editReservation.event;

      if (!event) {
        alert({
          title: 'Kein Event gefunden',
          description: 'Der gewählte Slot hat kein verbundenes Event mehr.',
          titleTranslation: 'reservations',
          descriptionTranslation: 'reservations',
          actions: [{ id: 'ok', label: 'Okay' }],
          onSubmit: () => {},
        });

        return false;
      }

      const ticket = normalizeTicketSelection(
        event,
        guests,
        editReservation.ticket
      );
      const ticketError = getTicketSelectionError(event, guests, ticket);

      if (ticketError) {
        alert({
          title: 'Ticket-Auswahl unvollständig',
          description: ticketError,
          titleTranslation: 'reservations',
          descriptionTranslation: 'reservations',
          actions: [{ id: 'ok', label: 'Okay' }],
          onSubmit: () => {},
        });

        return false;
      }

      seteditReservation((current) =>
        !current
          ? null
          : {
              ...current,
              ticket,
            }
      );

      return true;
    };

    if (editReservation?.waitinglistEntry) {
      const {
        guest,
        customId,
        source,
        createdBy,
        createdAt,
        customData = {},
      } = editReservation.waitinglistEntry;

      const reservation: Omit<RequestV03Document, 'updateNote'> = {
        restaurant: restaurant || '',
        guest: {
          ...guest,
          sendEmail: sendConfirmationEmail,
        },
        guests: guests || editReservation.waitinglistEntry.guests,
        date,
        time,
        reservationLength,
        startTimeInBlocks,
        endTimeInBlocks: startTimeInBlocks + reservationLength,
        customId,
        source,
        customData,
        occassion:
          editReservation.occassion ||
          editReservation.waitinglistEntry.occassion,
        space,
        tables,
        createdAt,
        createdBy,
        validTill: 0,
        sendConfirmationEmail,
        status: ReservationStatus.SUCCESS,
        fromWaitinglist: true,
      };

      const ref = db
        .collection('requestsV03')
        .doc(editReservation.reservationId);

      const entryRef = db
        .collection('waitinglistEntries')
        .doc(editReservation.reservationId);

      // The request can be canceled or accepted elsewhere while the form is
      // open, so its status is checked in the same transaction as the create.
      const blocked = await db.runTransaction(async (tx) => {
        const entryDoc = await tx.get(entryRef);
        const reservationDoc = await tx.get(ref);

        if (entryDoc.data()?.status !== RequestStatus.PENDING) return 'changed';

        if (reservationDoc.exists) return 'exists';

        tx.set(ref, {
          ...reservation,
          validTill: Date.now() + 15 * 60 * 1000,
          guest: EMPTY_GUEST,
        });

        return null;
      });

      if (blocked === 'changed') {
        seteditReservation(null);

        return toast(
          'This request was changed in the meantime.',
          Severity.INFO,
          'reservations'
        );
      }

      if (blocked === 'exists')
        return toast(
          'A Reservation allready exists for this Waitinglist entry.',
          Severity.ERROR,
          'reservations'
        );

      const updatedAt = DateNow();

      // One batch, so a request never stays open next to its reservation.
      const finalize = db.batch();

      finalize.update(ref, {
        guest: reservation.guest,
        validTill: 0,
        updatedBy: waiterSignature || actingAs(restaurant, updatedBy) || undefined,
        updatedAt,
        updateNote: {
          updatedAt,
          note: 'Reservierung wurde finalisiert',
        },
      });

      finalize.update(entryRef, {
        status: 'success',
        updatedAt,
        updatedBy: waiterSignature || actingAs(restaurant, updatedBy) || undefined,
      });

      try {
        await finalize.commit();
      } catch (error) {
        // A leftover placeholder would block every retry with "exists".
        await db
          .runTransaction(async (tx) => {
            const data = (await tx.get(ref)).data();

            if (data?.guest?.name === EMPTY_GUEST.name) tx.delete(ref);
          })
          .catch((err) => {
            console.error('Placeholder cleanup failed', err);
          });

        throw error;
      }

      seteditReservation(null);

      return;
    }

    if (editReservation?.new) {
      // New Reservation
      if (editReservation.walkin) {
        // @TODO: Load Settings Walkin Name Required

        reservationLength =
          editReservation.reservationLength ?? reservationLength;

        await db.collection('requestsV03').add({
          started: startTimeInBlocks,
          startTimeInBlocks,
          endTimeInBlocks: startTimeInBlocks + reservationLength,
          tables: tables || [],
          tableStr,
          time,
          reservationLength,
          date,
          guests,
          occassion,
          space,
          status: 'success',
          restaurant,
          guest: {
            name: `Walkin ${tables?.join(', ')}`,
            phone: '',
            email: '',
            hostComment: editReservation?.guest?.hostComment ?? '',
            comment: '',
            attr: [],
          },
          customId: createRandomId(),
          source: 'inHouse',
          validTill: 0,
          createdAt: DateNow(),
          createdBy: waiterSignature || actingAs(restaurant, updatedBy),
          walkIn: true,
        });

        seteditReservation(null);
      } else if (editReservation.page === 0) {
        if (!validateTicketSession()) return;

        let customId = createRandomId();

        if (editReservation.addToWaitinglist) {
          alert({
            title: 'Adding Guest to Waitinglist',
            description:
              'By Continueing you are placing the guest on the Waitinglist. No Reservation will be made. Make sure this has been communicated with the guest clearly.',
            descriptionTranslation: 'reservations',
            titleTranslation: 'reservations',
            actions: [
              { id: 'cancel', label: 'Cancel', onClick: () => {} },
              {
                id: 'submit',
                label: 'Continue',
                variant: 'primary',
                onClick: () => {
                  seteditReservation((eR) =>
                    eR === null ? null : { ...eR, page: 1, customId }
                  );
                },
              },
            ],
            onSubmit: () => {
              seteditReservation((eR) =>
                eR === null ? null : { ...eR, page: 1, customId }
              );
            },
          });

          return;
        }

        // @TODO: Pending Till

        let data = {
          startTimeInBlocks,
          endTimeInBlocks: startTimeInBlocks + reservationLength,
          tables,
          tableStr,
          time,
          reservationLength,
          date,
          guests,
          occassion,
          space,
          status: 'success',
          customId,
          restaurant,
          guest: EMPTY_GUEST,
          source: 'inHouse',
          validTill: DateNow() + 300000,
          createdAt: DateNow(),
          createdBy: waiterSignature || actingAs(restaurant, updatedBy),
        };

        if (!reservationId) {
          const { id } = await db.collection('requestsV03').add(data);

          reservationId = id;
        } else {
          await db.collection('requestsV03').doc(reservationId).set(data);
        }

        seteditReservation((eR) =>
          eR === null ? null : { ...eR, reservationId, page: 1, customId }
        );
      } else {
        if (!validateTicketSession()) return;

        let { guest, customData = {} } = editReservation;

        if (!guest || !guest.name) {
          // No TimeSlot Message
          alert({
            title: 'No Guest Information was entered!',
            titleTranslation: 'reservations',
            description: "You can't continue without the guest informations.",
            descriptionTranslation: 'reservations',
            onSubmit: () => {},
            actions: [
              {
                id: 'ok',
                label: 'Okay',
              },
            ],
          });

          return;
        }

        if (settings?.phoneNumberMandatory && guest.phone.length < 4) {
          alert({
            title: 'No Phone Number has been entered',
            titleTranslation: 'reservations',
            description: "You can't continue without the phone number.",
            descriptionTranslation: 'reservations',
            onSubmit: () => {},
            actions: [
              {
                id: 'ok',
                label: 'Okay',
              },
            ],
          });

          return;
        }

        if (settings?.emailMandatory && guest.email.length < 4) {
          alert({
            title: 'No Email entered',
            titleTranslation: 'reservations',
            description: 'You can not continue without an email.',
            descriptionTranslation: 'reservations',
            onSubmit: () => {},
            actions: [
              {
                id: 'ok',
                label: 'Okay',
              },
            ],
          });

          return;
        }

        if (settings?.signatureRequired && !waiterSignature) {
          alert({
            title: 'No Signature has been entered',
            titleTranslation: 'reservations',
            description: "You can't continue without the signature",
            descriptionTranslation: 'reservations',
            onSubmit: () => {},
            actions: [
              {
                id: 'ok',
                label: 'Okay',
              },
            ],
          });

          return;
        }

        console.log({ guest });

        if (guest?.id?.includes(`-${restaurant}`)) {
          guest.id = guest.id.replace(`-${restaurant}`, '');
        }

        // Mark before the writes, so a finalize still in flight is never
        // mistaken for an abandoned session.
        finalizedRef.current = reservationId ?? null;

        if (editReservation.addToWaitinglist) {
          let {
            time,
            flexibleTime = null,
            preferredFormOfContact = 'call',
          } = editReservation.addToWaitinglist;

          let waitinglistEntry: Omit<WaitingListEntryDocument, 'id'> = {
            date,
            restaurant: restaurant || '',
            guests,
            time,
            flexibleTime,
            preferredFormOfContact,
            guest,
            occassion: occassion || '',
            source: 'inHouse',
            customId: editReservation.customId,
            type: 'waitinglist',
            status: RequestStatus.PENDING,
            createdAt: DateNow(),
            customData,
            createdBy: waiterSignature || actingAs(restaurant, updatedBy) || '',
          };

          await db.collection('waitinglistEntries').add(waitinglistEntry);
        } else if (isTicketSessionMode(editReservation.ticketMode)) {
          await createTicketSession({ guest, customData });
        } else if (sendNoShowSMS) {
          await db
            .collection('requestsV03')
            .doc(reservationId)
            .update({
              guest,
              customData,
              updatedBy: waiterSignature || actingAs(restaurant, updatedBy),
              updatedAt: DateNow(),
              validTill: 0,
              updateNote: {
                updatedAt: DateNow(),
                note: 'No Show SMS wurde versendet',
              },
              noShowConfirmation: {
                validTill: DateNow() + 60 * 60000,
                sendVia: 'sms',
              },
            });
        } else {
          await db
            .collection('requestsV03')
            .doc(reservationId)
            .update({
              guest,
              customData,
              updatedBy: waiterSignature || actingAs(restaurant, updatedBy),
              updatedAt: DateNow(),
              updateNote: {
                updatedAt: DateNow(),
                note: 'Reservierung wurde finalisiert',
              },
              validTill: 0,
            });
        }

        seteditReservation(null);
      }
    } else {
      if (!validateTicketSession()) return;

      const ref = db.collection('requestsV03').doc(reservationId);

      let data: any = {
        time,
        startTimeInBlocks,
        endTimeInBlocks: startTimeInBlocks + reservationLength,
        guests,
        date,
        occassion,
        tables: tables || [],
        tableStr,
        updatedAt: DateNow(),
        updatedBy: waiterSignature || actingAs(restaurant, updatedBy),
        space,
        reservationLength,
        sendConfirmationEmail,
      };

      Object.keys(data).forEach((key) => {
        if (data[key] === undefined) {
          delete data[key];
        }
      });

      if (editReservation.clearValidTill) {
        // Saving an expired hold must free the doc: the edit payload
        // otherwise never touches validTill and the row stays locked.
        data.validTill = 0;
      }

      if (
        editReservation.customData &&
        Object.values(editReservation.customData).length > 0
      ) {
        Object.keys(editReservation.customData).forEach((key) => {
          data['customData.' + key] =
            (editReservation as any)?.customData?.[key] ?? '';
        });
      }

      // Each field is written only when the mobile edit dialog touched it, so
      // a save never reverts a comment someone changed elsewhere meanwhile.
      if (editReservation.commentEdited && editReservation.guest) {
        data['guest.comment'] = editReservation.guest.comment ?? '';
      }

      if (editReservation.hostCommentEdited && editReservation.guest) {
        data['guest.hostComment'] = editReservation.guest.hostComment ?? '';
      }

      await ref.update(data);

      if (isTicketSessionMode(editReservation.ticketMode)) {
        await createTicketSession();
      }

      seteditReservation(null);

      if (currentDate === date) {
        setcurrentReservation(reservationId || null);
      }
    }
  };

  const handleSubmit = async (
    signatureOverride?: string,
    pinChecked?: boolean
  ): Promise<void> => {
    let sendConfirmationEmail = false;

    console.log({ editReservation, settings });

    // onClick handlers may pass a SyntheticEvent as first arg — never
    // let a non-string reach Firestore (updatedBy/createdBy).
    const signature =
      typeof signatureOverride === 'string' ? signatureOverride : undefined;

    if (
      editReservation &&
      !editReservation.new &&
      !editReservation.waitinglistEntry &&
      pinChecked !== true
    ) {
      // Stays pending while the PIN dialog is open: the mobile Save button
      // locks on this promise.
      return new Promise<void>((resolve) =>
        authenticate(
          'reservations.editReservation.write',
          (pinSignature) =>
            handleSubmit(pinSignature || signature, true).finally(() =>
              resolve()
            ),
          () => resolve()
        )
      );
    }

    const waiterSignature = signature ?? editReservation?.waiterSignature;

    if (
      (!!editReservation?.page || !editReservation?.new) &&
      settings?.signatureRequired &&
      !waiterSignature
    ) {
      setSignaturePromptOpen(true);

      return;
    }

    if (
      (!editReservation?.new || !!editReservation?.waitinglistEntry) &&
      editReservation?.guest?.email &&
      editReservation.sendConfirmationEmail
    ) {
      sendConfirmationEmail = true;
    }

    if (!editReservation?.timeSlot || !editReservation.guests || !restaurant) {
      // No TimeSlot Message
      alert({
        title: 'No Time slot was selected!',
        titleTranslation: 'reservations',
        description:
          "You can't continue without a time for the reservation to start",
        descriptionTranslation: 'reservations',
        onSubmit: () => {},
        actions: [
          {
            id: 'ok',
            label: 'Okay',
          },
        ],
      });

      return;
    }

    const onSaveError = (error: any) => {
      // The finalize is marked before its writes, so a failed save has to give
      // the placeholder cleanup its session back.
      finalizedRef.current = null;

      toast(
        error?.response?.data?.message ??
          error?.message ??
          'Reservierung konnte nicht gespeichert werden.',
        Severity.ERROR
      );
    };

    if (
      !isLightReservation &&
      !editReservation.tables?.length &&
      !editReservation.addToWaitinglist
    ) {
      // No Tables Modal

      alert({
        title: 'No Tables where selected!',
        titleTranslation: 'reservations',
        description: 'Are you sure you want to continue?',
        descriptionTranslation: 'reservations',
        onSubmit: () =>
          submitReservation(sendConfirmationEmail, waiterSignature).catch(
            onSaveError
          ),
      });

      return;
    }

    try {
      await submitReservation(sendConfirmationEmail, waiterSignature);
    } catch (error: any) {
      onSaveError(error);
    }
  };

  const handleSignatureConfirmed = (signature: string) => {
    if (!validateSignature(signature)) return;

    setSignaturePromptOpen(false);

    seteditReservation((current) =>
      current ? { ...current, waiterSignature: signature } : current
    );

    handleSubmit(signature);
  };

  const closeSignaturePrompt = () => setSignaturePromptOpen(false);

  useEffect(() => {
    if (editReservation) {
      setoriginalUrl(editReservation.originalUrl ?? null);
    } else if (originalUrl) {
      goBackToOriginal();
    }
  }, [editReservation]);

  return {
    editReservation,
    seteditReservation,
    handleRelocate,
    handleSubmit,
    handleNewReservation,
    signaturePromptOpen,
    closeSignaturePrompt,
    handleSignatureConfirmed,
  };
};

export default useEditReservation;
